import {
  Address,
  BaseOrder,
  Cart,
  DetailProduct,
  LineItem,
  Order,
  ProductData,
} from '@interflora/ui-components/build/common/props'
import { convertCategoryNames, getAttribute } from '@interflora/ui-components/build/utils/common'
import { AlgoliaQueryType, ProductOrderFrom } from 'common/types'

interface AnalyticsOptions {
  gtmId: string
}

/**
 * Controls the population of Google Analytics' `dataLayer` object.
 */
export class Analytics {
  public currency = ''
  private readonly gtmId: string
  public subject: any = null
  private isBuffering = false
  private dataLayerBuffer: any[] = []
  private user_id: string | null | undefined = null
  private email: string | null = null

  /**
   * Construct the Analytics class.
   */
  constructor(options: AnalyticsOptions) {
    this.gtmId = options.gtmId
    if (!this.gtmId) {
      throw new Error('Analytics - No GTM ID provided to constructor')
    }
  }

  /**
   * Turn on buffering. This is generally only needed for when we transition
   * between pages. This is because the `page_view` event needs to be fired
   * before any other events when a new page is rendered.
   */
  bufferEvents() {
    console.debug('Analytics - turning on buffering')
    this.isBuffering = true
  }

  /**
   * Flush any buffered events to the `dataLayer` array.
   */
  flushEvents() {
    console.debug('Analytics - flushing buffered data layer')
    this.dataLayerBuffer.forEach((eventObject) => {
      console.debug('Analytics - adding to data layer from buffer', eventObject)
      window.dataLayer.push(eventObject)
    })
    this.dataLayerBuffer = []
    this.isBuffering = false
    console.debug('Analytics - buffering turned off')
  }

  fetchProductDescription = (product: DetailProduct) => {
    const {
      isInternational,
      isNational,
      isRegional,
      isDropShipPlant,
      isDropship,
      isSubscription,
      isFuneral,
      isOverseas,
    } = product

    // Priority-based product type checks
    if (isNational) return 'national'
    if (isRegional) return 'regional'
    if (isInternational) return 'international'
    if (isDropShipPlant) return 'plant'
    if (isDropship) return 'dropship'
    if (isSubscription) return 'subscription'
    if (isOverseas) return 'overseas'
    if (isFuneral) return 'funeral'
    if (product.typeKey === 'digital') return 'digital'

    // Check categories or attributes for Hamper/Plant before defaulting to Floral
    const categoryLower = (product.categoryNames ?? []).join(' ').toLowerCase()
    if (categoryLower.includes('hamper') || product.name.toLowerCase().includes('hamper')) return 'hamper'
    if (categoryLower.includes('plant') || product.name.toLowerCase().includes('plant')) return 'plant'

    return 'floral'
  }
  /**
   * Triggers a 'view_item` event on the dataLayer.
   */
  viewProduct(product: DetailProduct, indexName?: string, categoryNames?: string[]) {
    this.event('view_item', {
      items: [this.toEventProduct(product, undefined, categoryNames)],
      _clear: true,
      algoliaObjectIDs: product.id,
      algoliaIndexName: indexName,
      product_type: this.fetchProductDescription(product),
    })
  }

  /**
   * Triggers a 'view_item_list` event on the dataLayer.
   */
  viewProductList(data: { id: string; name: string; products: ProductData[]; categoryNames?: string[] }) {
    this.event('view_item_list', {
      items: data.products.map((product, index) => this.toEventProduct(product, index, data.categoryNames)),
      _clear: true,
      item_list_name: data.name,
      item_list_id: data.id,
    })
  }

  /**
   * Triggers a 'algoliaViewedFilters` event on the dataLayer.
   */
  algoliaViewedFilters(
    indexName: string,
    filters?: string,
    queryType?: AlgoliaQueryType,
    abTestID?: string,
    abTestVariantID?: string
  ) {
    this.event('algoliaViewedFilters', {
      algoliaIndexName: indexName,
      algoliaFilters: filters,
      algoliaEventNamePrefix: queryType ? `${queryType};` : null,
      ...(abTestID && { algoliaAbTestID: abTestID }),
      ...(abTestVariantID && { algoliaAbTestVariantID: abTestVariantID }),
    })
  }

  /**
   * Triggers a 'algoliaClickedFilters` event on the dataLayer.
   */
  algoliaClickedFilters(
    indexName?: string,
    filters?: string,
    queryType?: AlgoliaQueryType,
    abTestID?: string,
    abTestVariantID?: string
  ) {
    this.event('algoliaClickedFilters', {
      algoliaIndexName: indexName,
      algoliaFilters: filters,
      algoliaEventNamePrefix: queryType ? `${queryType};` : null,
      ...(abTestID && { algoliaAbTestID: abTestID }),
      ...(abTestVariantID && { algoliaAbTestVariantID: abTestVariantID }),
    })
  }

  /**
   * Triggers a ' SortBy facet filter buttons on our category pages' event on the dataLayer.
   */
  facetSortBy(value: string) {
    try {
      this.event(
        'sort_by',
        {
          value_selected: value,
        },
        true
      )
    } catch (err) {
      console.error(err)
    }
  }
  /**
   * Triggers a 'farEye Tracking order link' event on the dataLayer.
   */
  farEyeTrackOrder(url?: string) {
    try {
      this.event(
        'fareye_track_order_link',
        {
          fareye_track_order_link: url,
        },
        true
      )
    } catch (err) {
      console.error(err)
    }
  }
  /**
   * Triggers a 'filter buttons on our category pages' event on the dataLayer.
   */
  facetFilters(attribute: string, value: string) {
    try {
      this.event(
        'filters',
        {
          facet_name: attribute,
          facet_value: value,
        },
        true
      )
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * Triggers a 'view_cart' event on the dataLayer.
   */
  viewCart(cart?: Cart) {
    const items = this.convertBaseOrderItems(cart)
    try {
      this.event(
        'view_cart',
        {
          cart_id: cart?.id,
          items: items?.length ? items : null,
          _clear: true,
          value: cart?.totalPrice?.formatted?.slice(1) || 0,
          currency: this.currency,
        },
        true
      )
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * Triggers a 'checkout_step_progress' event on the dataLayer.
   */
  viewCheckout(cart?: Cart) {
    const items = this.convertBaseOrderItems(cart)
    const shipping_tier =
      cart?.consignments?.find((consignment) => consignment?.delivery?.serviceCode)?.delivery?.serviceCode || null

    try {
      this.event(
        'view_checkout',
        cart
          ? {
              items: items?.length ? items : null,
              _clear: true,
              value: cart.totalPrice?.formatted?.slice(1) || 0,
              currency: this.currency,
              shipping_tier,
              coupon: cart?.couponCode || null,
            }
          : {}
      )
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * Triggers a 'checkout_step_progress' event on the dataLayer.
   */
  purchaseOrder(order: Order) {
    const items = this.convertBaseOrderItems(order)
    const enhancedConversionData = this.getEnhancedConversionData(order.billingAddress)
    try {
      this.event(
        'purchase',
        {
          items: items?.length ? items : null,
          _clear: true,
          value: order.totalPrice?.formatted?.slice(1) || 0,
          currency: this.currency,
          transaction_id: order?.orderNumber,
          shipping: order.deliveryCost?.formatted?.slice(1) || 0,
          coupon: order.couponCode || null,
          enhancedConversionData,
          discount: order.couponDiscount?.formatted?.slice(1) || 0,
        },
        true
      )
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * Triggers a 'view_page` event on the dataLayer. When a new page
   * is loaded, this should be fired before any other events in order
   * for those other events to be associated with the correct page. This
   * is the purpose of the buffering functionality in this class.
   */
  viewPage(data: any) {
    this.event('page_view', data, true)
  }

  /**
   * Triggers an 'add_to_order' event on the dataLayer.
   */
  addToOrder(detail: any) {
    try {
      this.event('add_to_order', detail)
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * Triggers an 'add_to_cart' event on the dataLayer. Can override the quantity on the item if specified.
   */
  addToCart(
    cart_id: string | undefined,
    item: LineItem,
    quantity?: number,
    queryId?: string,
    indexName?: string,
    sticky_cta?: string
  ) {
    const items = [this.toEventLineItem(item, quantity)]
    try {
      this.event('add_to_cart', {
        cart_id,
        items,
        _clear: true,
        algoliaObjectIDs: item.productId,
        algoliaQueryID: queryId,
        algoliaIndexName: indexName,
        sticky_cta,
      })
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * Triggers a 'remove_to_cart' event on the dataLayer. Can override the quantity on the item if specified.
   */
  removeFromCart(cart_id: string | undefined, item: LineItem, quantity?: number) {
    const items = [this.toEventLineItem(item, quantity)]
    try {
      this.event('remove_from_cart', { cart_id, items, _clear: true })
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * Triggers a 'login' event on the dataLayer.
   */
  signIn(user_id: string | null | undefined, email: string) {
    try {
      this.event('login', { method: 'commercetools' })
      this.setUserDetails(user_id, email)
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * Triggers a 'logout' event on the dataLayer.
   */
  signOut() {
    try {
      this.event('logout', { method: 'commercetools' })
      this.setUserDetails(null, null)
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * Triggers a 'register' event on the dataLayer.
   */
  register() {
    try {
      this.event('register', { method: 'commercetools' })
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * Sets the user id on the data layer
   */
  setUserDetails(user_id: string | null | undefined, email: string | null) {
    if (this.user_id !== user_id || this.email !== email) {
      try {
        this.event('user_updated', { user_id, email })
        this.user_id = user_id
        this.email = email
      } catch (err) {
        console.error(err)
      }
    }
  }

  /**
   *   Triggers a 'pdpPath' event on the dataLayer.
   */
  pdpPath(delivery_date: string, postcode: string | null | undefined, journey_type: string) {
    try {
      this.event('pdp_path', {
        delivery_date: delivery_date,
        postcode: postcode,
        journey_type: journey_type,
      })
    } catch (err) {
      console.error(err)
    }
  }

  /**
   *   Triggers a 'Old Calendar Pdp Path' event on the dataLayer.
   */
  oldCalendarJourney(delivery_date: string, postcode: string | null | undefined, journey_type: string) {
    try {
      this.event('old_calendar_journey', {
        delivery_date: delivery_date,
        postcode: postcode,
        journey_type: journey_type,
      })
    } catch (err) {
      console.error(err)
    }
  }

  oldCalendarUnavailable() {
    try {
      this.event('old_calendar_unavailable', {})
    } catch (err) {
      console.error(err)
    }
  }

  productVariantChange(sku: string) {
    try {
      this.event('product_variant_change', {
        productVariantId: sku,
      })
    } catch (err) {
      console.error(err)
    }
  }

  addressErrorEvent(data: any) {
    try {
      this.event('address_error_event', {
        first_name: data?.firstName,
        last_name: data?.lastName,
        phone_number: data?.phone,
        recipient_address: data?.recipientAddress,
      })
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * Triggers an event when user enter price lessthan minimum open price
   */
  minOpenPriceErrorEvent(variant: string, data: any) {
    try {
      this.event('min_open_price_error', {
        variant,
        min_open_price_value: data,
      })
    } catch (err) {
      console.error(err)
    }
  }
  /**
   * Triggers an event when user enter price morethan 499
   */
  maxOpenPriceErrorEvent(variant: string, data: any) {
    try {
      this.event('max_open_price_error', {
        variant,
        max_open_price_value: data,
      })
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * Triggers an event when user doesn't enter any value
   */
  openPriceErrorEvent(variant: string, data: any) {
    try {
      this.event('open_price_error', {
        variant,
        open_price_error: data,
      })
    } catch (err) {
      console.error(err)
    }
  }
  /**
   * Triggers an event when user enter valid openPrice
   */
  openPriceEvent(variant: string, data: any) {
    try {
      this.event('open_price_event', {
        variant,
        open_price_value: data,
      })
    } catch (err) {
      console.error(err)
    }
  }

  /**
   *   Triggers a 'selected product from' event on the dataLayer.
   */
  productOrderedFrom(item: ProductOrderFrom) {
    try {
      this.event('product_ordered_from', {
        product_name: item.productName,
        product_ordered: item.productOrderedFrom,
      })
    } catch (err) {
      console.error(err)
    }
  }

  /**
   *   Triggers an event when user provides a personal message during order flow
   */
  personalMessageLocation(location: string) {
    try {
      this.event('personal_message_box_location', {
        personal_message_location: location,
      })
    } catch (err) {
      console.error(err)
    }
  }

  pdpThumbnailClick(position: number) {
    try {
      this.event(
        'pdp_thumbnail_click',
        {
          pdp_thumbnail_position: position,
        },
        true
      )
    } catch (err) {
      console.error(err)
    }
  }

  pdpThumbnailNavigation() {
    try {
      this.event('pdp_thumbnail_navigation', null)
    } catch (err) {
      console.error(err)
    }
  }
  pdpThumbnailClose() {
    try {
      this.event('pdp_thumbnail_close', null)
    } catch (err) {
      console.error(err)
    }
  }
  /**
   *   Triggers a 'selected product from' event on the dataLayer.
   */
  addThisGiftForAnotherPersonBasket(add_this_gift_for_another_person_basket: string) {
    try {
      this.event('add_this_gift_for_another_person_basket', {
        add_this_gift_for_another_person_basket,
      })
    } catch (err) {
      console.error(err)
    }
  }

  alternateProductCalendarJourney(date: string) {
    try {
      this.event('alternate_product_calendar_journey', {
        delivery_date: date,
      })
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * Triggers a IDP event journey click
   */
  idpJourney(journey_path: string, idp_product_type: string) {
    try {
      this.event('idp_journey', {
        idp_journey_path: journey_path,
        idp_product_type: idp_product_type,
      })
    } catch (err) {
      console.error(err)
    }
  }
  /**
   * Occasion_Reminder  event
   *
   */

  occasions(occasion: string[], occasionPage: string) {
    try {
      this.event('occasion_reminder', {
        occasion_set: occasion,
        occasion_reminder_creation_page: occasionPage,
        occasion_reminder_count: occasion.length,
      })
    } catch (err) {
      console.error(err)
    }
  }
  /**
   * Triggers a Search Redirect event on a data layer.
   */
  searchRedirect(url: string) {
    try {
      this.event('search_redirect', {
        redirect_url: url,
      })
    } catch (err) {
      console.error(err)
    }
  }

  searchTerm(value: string) {
    try {
      this.event('search_term', {
        search_value: value,
      })
    } catch (err) {
      console.error(err)
    }
  }

  searchOverlayNoResults(value: string) {
    try {
      this.event('search_overlay_no_results', {
        message: value,
      })
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * Responsible for actually appending/pre-pending an event on to
   * the `dataLayer` object.
   */
  event(name: string, data: any, prependIfBuffering = false) {
    if (!window?.dataLayer) {
      console.warn(`'No 'window.dataLayer' to add event '${name}' to with data: `, data)
      return
    }
    const eventObject = {
      ...data,
      event: name || undefined,
    }
    if (!this.isBuffering) {
      console.debug('Analytics - adding to data layer', eventObject)
      window.dataLayer.push(eventObject)
    } else {
      console.debug(`Analytics - buffering event${prependIfBuffering ? ' (prepended)' : ''}`, eventObject)
      if (prependIfBuffering) {
        this.dataLayerBuffer.unshift(eventObject)
      } else {
        this.dataLayerBuffer.push(eventObject)
      }
    }
  }

  /**
   * Ensure that line items are converted in to a consistent and expected format.
   */
  private toEventLineItem(lineItem: LineItem, quantity?: number) {
    const price = lineItem.price?.formatted.slice(1)
    const color = getAttribute('variantColor', lineItem.attributes)
    const size = getAttribute('variantSize', lineItem.attributes)
    const item_variant = [color, size].filter(Boolean).join(', ') || null
    const categoryNames = lineItem.category
      ? [
          lineItem.category.name,
          ...(lineItem.category.ancestors
            ?.slice()
            .reverse()
            .map((category) => category.name) || []),
        ]
      : null

    return {
      currency: this.currency,
      item_id: lineItem.product?.key || '',
      item_name: lineItem.product?.name || lineItem.name,
      item_variant,
      price,
      quantity: typeof quantity === 'number' ? quantity : lineItem.quantity || 1,
      c_item_variant_name: lineItem.name || null,
      c_item_variant_id: lineItem.sku || null,
      ...convertCategoryNames(categoryNames || lineItem?.product?.categoryNames),
      // affiliation: '<Affiliation Reference>', // not in Beta
    }
  }

  /**
   * Creates enhanced conversion data from order billing information
   * for use with Google Ads Enhanced Conversions
   */
  private getEnhancedConversionData(billingAddress: Address) {
    // Concatenate street address lines (removing empty strings)
    const streetLines = [billingAddress.addressLine1, billingAddress.addressLine2, billingAddress.addressLine3].filter(
      (line) => line && line.trim() !== ''
    )

    const streetAddress = streetLines.join(', ')

    // Format phone number to E.164 format (e.g., +447123456789)
    let formattedPhone = null
    if (billingAddress.phone) {
      // Remove all non-digit characters except '+'
      const cleanedPhone = billingAddress.phone.replace(/[^\d+]/g, '')

      // If phone already starts with +, use it as is
      if (cleanedPhone.startsWith('+')) {
        formattedPhone = cleanedPhone
      } else {
        // Add country code based on billing address country
        const countryCode = this.getCountryCodeByCountry(billingAddress.country)
        formattedPhone = countryCode ? `+${countryCode}${cleanedPhone.replace(/^0+/, '')}` : null
      }

      // Validate that the number has 11-15 digits (including country code)
      if (formattedPhone && !/^\+\d{10,14}$/.test(formattedPhone)) {
        formattedPhone = null // Invalid format, don't send
      }
    }

    // Format the data according to Google Ads Enhanced Conversion requirements
    const enhancedConversionData: Record<string, any> = {
      email: billingAddress.email ?? null,
      phone_number: formattedPhone,
      address: {
        first_name: billingAddress.firstName ?? null,
        last_name: billingAddress.lastName ?? null,
        street: streetAddress ?? null,
        city: billingAddress.city ?? null,
        region: billingAddress.county ?? null,
        postal_code: billingAddress.postalCode ?? null,
        country: billingAddress.country ?? null,
      },
    }

    // Remove any null values to ensure clean data
    Object.keys(enhancedConversionData).forEach((key) => {
      if (enhancedConversionData[key] === null) {
        delete enhancedConversionData[key]
      } else if (typeof enhancedConversionData[key] === 'object') {
        Object.keys(enhancedConversionData[key]).forEach((subKey) => {
          if (enhancedConversionData[key][subKey] === null || enhancedConversionData[key][subKey] === '') {
            delete enhancedConversionData[key][subKey]
          }
        })
        // If the address object is now empty, remove it
        if (Object.keys(enhancedConversionData[key]).length === 0) {
          delete enhancedConversionData[key]
        }
      }
    })

    return enhancedConversionData
  }

  /**
   * Helper function to get the country calling code by country ISO code
   */
  private getCountryCodeByCountry(countryIso?: string) {
    const countryCodes: Record<string, string> = {
      GB: '44', // United Kingdom
      IE: '353', // Ireland
    }

    return countryCodes[countryIso ?? ''] ?? null
  }

  /**
   * Ensure that products are converted in to a consistent and expected format.
   */
  private toEventProduct(product: ProductData | DetailProduct, index?: number, categoryNames?: string[]) {
    const masterVariant = (product as DetailProduct).variants?.find((v) => v.isMaster)
    const price = product.price.formatted.slice(1)
    const color = getAttribute('variantColor', masterVariant?.attributes)
    const size = getAttribute('variantSize', masterVariant?.attributes)
    const item_variant = [color, size].filter(Boolean).join(', ') || null
    return {
      currency: this.currency,
      item_id: product.key,
      item_name: product.name,
      item_variant,
      price,
      quantity: 1,
      index,
      c_item_variant_name: masterVariant?.name || null,
      c_item_variant_id: masterVariant?.sku || null,
      ...convertCategoryNames(categoryNames || (product as DetailProduct)?.categoryNames),
    }
  }

  applyCodeUsage(applyCodeCount: number, status: string, promoCode?: string) {
    try {
      this.event('apply_code_usage', {
        discount_code_count: applyCodeCount,
        discount_code_status: status,
        discount_code_used: promoCode,
      })
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * Convert base order consignment line items and finishing touches into event items
   */
  convertBaseOrderItems = (order?: BaseOrder) =>
    order?.consignments?.reduce<ReturnType<Analytics['toEventLineItem']>[]>(
      (acc, consignment) => [
        ...acc,
        ...consignment.items.reduce<ReturnType<Analytics['toEventLineItem']>[]>(
          (acc, itemGroup) => [
            ...acc,
            this.toEventLineItem(itemGroup.lineItem),
            ...(itemGroup.finishingTouchLineItems ?? []).map((ft) => this.toEventLineItem(ft)),
          ],
          []
        ),
      ],
      []
    )

  /** save category spotlight banner */
  categorySpotLightBanner(click_text: string, category: string) {
    try {
      this.event('category_spotlight', {
        click_text,
        category,
      })
    } catch (err) {
      console.error(err)
    }
  }
  menuDrillDownClick(drilldown_text: string) {
    try {
      this.event('menu_drilldown_click', {
        drilldown_text,
      })
    } catch (err) {
      console.error(err)
    }
  }
  menulinkClick(menu_link_text: string) {
    try {
      this.event('menu_link_clicked', {
        menu_link_text,
      })
    } catch (err) {
      console.log(err)
    }
  }
  postCodeCheckerAddressSuccess(pcc_postcode: string, pcc_city: string, pcc_county: string) {
    try {
      this.event('pcc_address_success', {
        pcc_postcode,
        pcc_city,
        pcc_county,
      })
    } catch (err) {
      console.error(err)
    }
  }

  postCodeCheckerAddressManual() {
    try {
      this.event('pcc_address_manual', {
        pcc_manual_address: true,
      })
    } catch (err) {
      console.error(err)
    }
  }

  postCodeCheckerOccasionSelected(occasion: string) {
    try {
      this.event('pcc_occasion_selected', {
        pcc_selected_occasion: occasion,
      })
    } catch (err) {
      console.error(err)
    }
  }

  postCodeCheckerComplete() {
    try {
      this.event('pcc_complete', {
        pcc_submit: true,
      })
    } catch (err) {
      console.error(err)
    }
  }
  postCodeCheckerOpen() {
    try {
      this.event('pcc_open', {
        pcc_click: true,
      })
    } catch (err) {
      console.error(err)
    }
  }

  postCodeCheckerDate(date?: string) {
    try {
      this.event('pcc_date_selected', {
        delivery_date: date,
      })
    } catch (err) {
      console.error(err)
    }
  }

  trackEnterManualAddressClick(click_location: string) {
    try {
      this.event('manual_address_click', {
        manual_address_location: click_location,
      })
    } catch (err) {
      console.error(err)
    }
  }

  trackAvailabilityCheckerBanner(click_name: string, extraData?: Record<string, string>) {
    try {
      this.event('banner_pcc', {
        click_name,
        ...extraData,
      })
    } catch (err) {
      console.error(err)
    }
  }

  signInJourney(isSignInSuccess: boolean, sign_in_location: string) {
    try {
      this.event('sign_in', {
        sign_in_successful: isSignInSuccess,
        sign_in_unsuccessful: !isSignInSuccess,
        sign_in_location,
      })
    } catch (error) {
      console.error(error)
    }
  }

  continueAsGuest() {
    try {
      this.event('continue_as_guest', {
        continue_as_guest: true,
      })
    } catch (error) {
      console.error(error)
    }
  }

  createAnAccount(isCreateAccountSuccess: boolean) {
    try {
      this.event('create_an_account', {
        create_an_account_successful: isCreateAccountSuccess,
        create_an_account_unsuccessful: !isCreateAccountSuccess,
      })
    } catch (error) {
      console.error(error)
    }
  }
  paymentSelection(payment_selection_type: string, isPaymentSuccess: boolean) {
    try {
      this.event('payment_selection', {
        payment_selection_type,
        payment_success: isPaymentSuccess,
        payment_fail: !isPaymentSuccess,
      })
    } catch (err) {
      console.error(err)
    }
  }
  klarnaLearnMoreClick() {
    try {
      this.event('klarna_learn_more', {
        klarna_info_click: true,
      })
    } catch (error) {
      console.error(error)
    }
  }
  paypalLearnMoreClick() {
    try {
      this.event('pdp_payment_info_click', {})
    } catch (error) {
      console.error(error)
    }
  }
  pccPath(pcc_product_availability: string) {
    try {
      this.event('pcc_path', {
        pcc_product_availability,
      })
    } catch (err) {
      console.error(err)
    }
  }

  sendPromoContentGalleryAnalytics(linkText: string) {
    try {
      this.event('promo_content_gallery_click', {
        promo_content_gallery_click_text: linkText,
      })
    } catch (err) {
      console.log(err)
    }
  }

  doYouWantInternationalDelivery(attribute: string) {
    try {
      this.event(
        'do_you_want_international_delivery',
        {
          click_location: attribute,
        },
        true
      )
    } catch (err) {
      console.error(err)
    }
  }

  sendLinkGalleryTextAnalytics(linkText: string) {
    try {
      this.event('link_gallery_click', {
        link_gallery_click_text: linkText,
      })
    } catch (err) {
      console.log(err)
    }
  }

  sendCategoryCarouselClickAnalytics(click_text: string) {
    try {
      this.event('category_carousel_click', {
        click_text,
      })
    } catch (err) {
      console.log(err)
    }
  }

  categoryCarouselClick(click_text: string) {
    this.sendCategoryCarouselClickAnalytics(click_text)
  }

  blogArticleGalleryAnalytics(attribute: string) {
    try {
      this.event('blog_article_gallery_click', {
        blog_link_text_url: attribute,
      })
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * Triggers a 'HomeHeroBanner' event on the dataLayer.
   */
  homeHeroBanner(category: string) {
    try {
      this.event(
        'home_hero_banner_click',
        {
          home_hero_banner_click_text: category,
        },
        true
      )
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * Triggers a 'Related Content Gallery link click' event on the dataLayer.
   */
  sendRelatedContentGalleryAnalytics(linkText: string) {
    try {
      this.event('related_content_gallery_click', {
        related_content_gallery_click_text: linkText,
      })
    } catch (err) {
      console.log(err)
    }
  }

  /**
   * Triggers a 'Footer Link Gallery link click' event on the dataLayer.
   */
  sendFooterAnalytics(linkText: string) {
    try {
      this.event('footer_click', {
        footer_link_gallery_click_text: linkText,
      })
    } catch (err) {
      console.log(err)
    }
  }

  userLoginStatusAnalytics(loginStatus: string) {
    try {
      this.event('login_status', {
        login_status: loginStatus,
      })
    } catch (err) {
      console.log(err)
    }
  }

  editColor() {
    try {
      this.event('edit_colour', {})
    } catch (err) {
      console.error(err)
    }
  }
  editSize() {
    try {
      this.event('edit_size', {})
    } catch (err) {
      console.error(err)
    }
  }
  editStyle() {
    try {
      this.event('edit_style', {})
    } catch (err) {
      console.error(err)
    }
  }
  editSpecialInstructions() {
    try {
      this.event('edit_instructions', {})
    } catch (err) {
      console.error(err)
    }
  }
  contentFeatureLinkTextAnalytics(clickText: string) {
    try {
      this.event('content_feature_click', {
        content_feature_click_text: clickText,
      })
    } catch (err) {
      console.log(err)
    }
  }
  editRecipientAddress() {
    try {
      this.event('edit_recipient_address', {})
    } catch (err) {
      console.error(err)
    }
  }
  newsLetterSignUpOpen(location: string) {
    try {
      this.event('news_signup_open', {
        news_location: location,
      })
    } catch (err) {
      console.error(err)
    }
  }
  newsLetterSignUpSubmit(location: string) {
    try {
      this.event('news_signup_complete', {
        news_location: location,
      })
    } catch (err) {
      console.error(err)
    }
  }

  addPersonalisedMessage(occasion: string) {
    try {
      this.event('pdp_message_suggestion_click', {
        pdp_suggestion_occasion: occasion,
      })
    } catch (err) {
      console.error(err)
    }
  }

  clickAndCollectCloseModal() {
    try {
      this.event('click_and_collect_close_modal', {})
    } catch (err) {
      console.error(err)
    }
  }

  clickAndCollectLoadMore() {
    try {
      this.event('click_and_collect_load_more', {})
    } catch (err) {
      console.error(err)
    }
  }
  private fireQuizEvent(eventName: string) {
    try {
      this.event(eventName, {})
    } catch (error) {
      console.error(error)
    }
  }

  quizStarted() {
    this.fireQuizEvent('quiz_started')
  }

  quizQuestionAnswered(questionNumber: number) {
    this.fireQuizEvent(`quiz_question_${questionNumber}_answered`)
  }

  quizMarketingPageCompleted() {
    this.fireQuizEvent('quiz_marketing_page_completed')
  }

  clickAndCollectMapOrListView() {
    try {
      this.event('click_and_collect_map_or_list_view', {})
    } catch (err) {
      console.error(err)
    }
  }

  clickAndCollectSelectFlorist(florist_selected: string, position_in_list: number) {
    try {
      this.event('click_and_collect_select_florist', {
        florist_selected,
        position_in_list,
      })
    } catch (err) {
      console.error(err)
    }
  }

  // Post Code Checker Closed Location Analytics
  trackPostCodeCheckerClose(location: string) {
    try {
      this.event('pcc_close', {
        location,
      })
    } catch (err) {
      console.error(err)
    }
  }

  clickAndCollectStartNewSearch() {
    try {
      this.event('click_and_collect_start_new_search', {})
    } catch (err) {
      console.error(err)
    }
  }

  keepShoppingTracking() {
    try {
      this.event('keep_shopping', {})
    } catch (err) {
      console.error(err)
    }
  }

  pccEditFeatureTracking(pcc_edit_location: string) {
    try {
      this.event('pcc_edit', { pcc_edit_location })
    } catch (err) {
      console.error(err)
    }
  }

  trackPCCStartNewSearch() {
    try {
      this.event('pcc_start_new_search', {
        pcc_start_new_search: true,
      })
    } catch (err) {
      console.error(err)
    }
  }

  trackIDPWithinBasket(clickedButton: string) {
    try {
      this.event('idp_within_basket', {
        button_clicked: clickedButton,
      })
    } catch (err) {
      console.error(err)
    }
  }
  birthdayCampaignEntryTracking(competition_entry_status: string) {
    try {
      this.event('enter_competition', {
        competition_entry_status,
      })
    } catch (err) {
      console.error(err)
    }
  }

  favouritingProductItem(item_name: string, sticky_cta: string = 'N') {
    try {
      this.event('item_favourited', {
        item_name,
        sticky_cta,
      })
    } catch (err) {
      console.error(err)
    }
  }

  pdpFloristRequestsOpen() {
    try {
      this.event('pdp_florist_requests_open', {})
    } catch (err) {
      console.error(err)
    }
  }

  pdpFloristRequestComplete() {
    try {
      this.event('pdp_florist_request_complete', {})
    } catch (err) {
      console.error(err)
    }
  }

  pdpOpenAccordion(click_text: string) {
    try {
      this.event('pdp_open_accordion', { click_text: click_text })
    } catch (err) {
      console.error(err)
    }
  }

  pdpTrustpilotClick() {
    try {
      this.event('pdp_trustpilot_click', {})
    } catch (err) {
      console.error(err)
    }
  }
}
